<script setup lang="ts">
import { useGlobalStore } from "@/store/globals.ts";
import { useDropdown } from "@/composables/useDropdown.ts";
import { themes, useThemeStore } from "@/store/themeStore.ts";

const { hasDarkBackground = true } = defineProps<{
  hasDarkBackground?: boolean;
}>();

const themeStore = useThemeStore();
const { isOpen, modalRef, toggleDropdown } = useDropdown();
const globals = useGlobalStore();
</script>

<template>
  <div class="relative">
    <button
      id="themeDropdown"
      class="block items-center rounded-lg px-4 py-2 transition-colors duration-200"
      :class="{
        'hover:bg-cs-900-lighter ui-open:outline ui-open:outline-cs-300 text-gray-100':
          globals.isDesktop,
        'focus:outline-cs-300 focus:outline': globals.isDesktop && isOpen,
        'dark:bg-cs-950 bg-gray-100 text-black hover:bg-gray-200 dark:text-gray-100 dark:hover:bg-gray-700':
          !globals.isDesktop,
      }"
      @click="toggleDropdown"
    >
      <span
        class="inline pr-1.5 text-lg [@media(min-width:0960px)]:hidden [@media(min-width:1030px)]:inline"
        >Theme</span
      >
      <IconFA
        :icon="themeStore.current.icon"
        class="size-[1.125rem]"
        :class="{
          'text-cs-100': hasDarkBackground,
          'text-gray-400': !hasDarkBackground,
        }"
      />
    </button>
    <Transition
      enter-active-class="transition-opacity duration-200 ease"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-200 ease"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-show="isOpen"
        ref="modalRef"
        class="dark:border-cs-950 dark:bg-cs-900 focus:outline-hidden absolute -right-10 z-10 mt-1 flex w-48 flex-col rounded-lg border border-gray-200 bg-white text-gray-600 shadow-lg dark:text-gray-300"
        :class="{ '-left-10 -top-32': !globals.isDesktop }"
      >
        <button
          v-for="theme in Object.values(themes)"
          :key="theme.id"
          class="hover:bg-cs-200 dark:hover:bg-cs-900-lighter group flex w-full items-center px-4 py-2 text-sm text-black transition-colors duration-200 first:rounded-t-lg last:rounded-b-lg dark:text-gray-300"
          :class="{
            'bg-cs-100 dark:bg-cs-600 dark:text-white':
              theme.id === themeStore.current.id,
          }"
          @click="themeStore.select(theme.id)"
        >
          <IconFA
            :icon="theme.icon"
            class="mr-3 size-5 group-hover:text-gray-500 dark:group-hover:text-gray-200"
            :style="{ color: theme.color }"
          />
          <span>{{ theme.name }}</span>
        </button>
      </div>
    </Transition>
  </div>
</template>
